<template>
  <div class="pdp-grid">
    <div class="pdp-grid__header">
      <div class="pdp-grid__count">
        <b>{{ tiles.length }}</b> {{ tiles.length === 1 ? 'image' : 'images' }}
        <span
          v-if="generatingCount > 0"
          class="pdp-grid__generating"
        > · {{ generatingCount }} generating</span>
      </div>
      <div class="pdp-grid__actions">
        <button
          v-if="showStop"
          type="button"
          class="pdp-grid__stop"
          @click="act('pdp_grid_stop')"
        >
          <i class="bi bi-x-lg" /> Stop
        </button>
        <button
          type="button"
          class="pdp-grid__btn"
          :disabled="!canCreate"
          @click="act('create_pdp_mixed_layout:false')"
        >
          Create Layout
        </button>
        <button
          v-if="grid.hasAiOrScene"
          type="button"
          class="pdp-grid__btn pdp-grid__btn--primary"
          :disabled="!canCreate"
          @click="act('create_pdp_mixed_layout:true')"
        >
          Create Editable Layout
        </button>
      </div>
    </div>

    <div class="pdp-grid__tiles">
      <div
        v-for="tile in tiles"
        :key="tile.rowIndex"
        class="pdp-grid__tile"
        :class="`pdp-grid__tile--${tile.state}`"
        @mouseenter="hovered = tile.rowIndex"
        @mouseleave="hovered = null"
      >
        <span
          class="pdp-grid__badge"
          :class="{
            'pdp-grid__badge--gallery': tile.mode === 'gallery',
            'pdp-grid__badge--plain': isUseAsIs(tile),
          }"
        >{{ badgeText(tile) }}</span>

        <div
          v-if="canToggleProductMode(tile) && hovered === tile.rowIndex && !grid.stopped"
          class="pdp-grid__mode"
          @click.stop
        >
          <button
            type="button"
            class="pdp-grid__mode-btn"
            title="Layered: regenerates this image with a clean background (no product), then adds the product as a separate, editable layer when you create the layout."
            :class="{ 'is-active': tileProductMode(tile) === 'separate' }"
            @click.stop="act(`pdp_grid_mode:${tile.rowIndex}:separate`)"
          >
            Layered
          </button>
          <button
            type="button"
            class="pdp-grid__mode-btn"
            title="Static: regenerates this image with the product composited in (flat, not an editable layer)."
            :class="{ 'is-active': tileProductMode(tile) === 'integrated' }"
            @click.stop="act(`pdp_grid_mode:${tile.rowIndex}:integrated`)"
          >
            Static
          </button>
        </div>

        <template v-if="tile.state === 'done' && tile.url">
          <img
            :src="tile.url"
            :alt="tile.label"
            class="pdp-grid__img"
            @click="openTile(tile)"
          >
          <img
            v-if="tile.overlayUrl"
            :key="tile.overlayUrl"
            :src="tile.overlayUrl"
            class="pdp-grid__overlay"
            alt="Product"
          >
          <div
            v-if="tile._mrhiCallouts && tile._mrhiCallouts.type === 'cambridge'"
            class="pdp-grid__callouts"
          >
            <div class="pdp-grid__callout-flavor">
              {{ tile._mrhiCallouts.flavorText }}
            </div>
            <div class="pdp-grid__callout-count">
              {{ tile._mrhiCallouts.countText }}
            </div>
          </div>
        </template>

        <div
          v-else
          class="pdp-grid__placeholder"
        >
          <i
            class="bi bi-stars"
            :class="{
              'pdp-grid__spark--active': tile.state === 'generating',
              'pdp-grid__spark--queued': tile.state === 'queued' || tile.state === 'stopped',
            }"
          />
          <span class="pdp-grid__label">{{ tile.label }}</span>
        </div>

        <div
          v-if="tile.state === 'done' && hovered === tile.rowIndex && !grid.stopped"
          class="pdp-grid__hover"
        >
          <button
            type="button"
            class="pdp-grid__hover-btn"
            @click.stop="act(`pdp_grid_regenerate:${tile.rowIndex}`)"
          >
            <i class="bi bi-arrow-counterclockwise" /> Regenerate
          </button>
          <button
            type="button"
            class="pdp-grid__hover-x"
            @click.stop="act(`pdp_grid_skip:${tile.rowIndex}`)"
          >
            ✕
          </button>
        </div>

        <div
          v-if="tile.state === 'regenerating'"
          class="pdp-grid__tile-spinner"
        >
          <i class="bi bi-arrow-repeat spin" />
        </div>
        <div
          v-if="tile.state === 'skipped' || tile.state === 'stopped'"
          class="pdp-grid__skipped"
        >
          {{ tile.state === 'stopped' ? 'Stopped' : 'Skipped' }}
        </div>
        <div
          v-if="tile.state === 'error'"
          class="pdp-grid__error"
        >
          Failed
          <button
            type="button"
            class="pdp-grid__hover-btn"
            @click.stop="act(`pdp_grid_retry:${tile.rowIndex}`)"
          >
            Retry
          </button>
        </div>
      </div>

      <div
        v-if="addableTypes.length"
        class="pdp-grid__tile pdp-grid__add-tile"
      >
        <button
          ref="addTrigger"
          type="button"
          class="pdp-grid__add-trigger"
          :class="{ 'is-open': addableTypesOpen }"
          @click="toggleAddDropdown"
        >
          <i class="bi bi-plus-lg" />
          <span>Add image type</span>
        </button>
        <div
          v-if="addableTypesOpen"
          class="pdp-grid__add-dropdown-menu"
          :class="{ 'pdp-grid__add-dropdown-menu--down': dropdownOpensDown }"
        >
          <div class="pdp-grid__add-dropdown-header">
            Add more to this set
          </div>
          <button
            v-for="t in addableTypes"
            :key="t.key"
            type="button"
            class="pdp-grid__add-item"
            @click="addType(t.key)"
          >
            <i class="bi bi-plus-lg" /> {{ t.label }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="lightboxTile"
      class="pdp-grid__lightbox"
      @click.self="closeLightbox"
    >
      <button
        type="button"
        class="pdp-grid__lightbox-download"
        title="Download image"
        @click="downloadLightboxImage"
      >
        <i class="bi bi-download" />
      </button>
      <button
        type="button"
        class="pdp-grid__lightbox-close"
        @click="closeLightbox"
      >
        ✕
      </button>
      <button
        v-if="hasPrev"
        type="button"
        class="pdp-grid__lightbox-nav pdp-grid__lightbox-nav--prev"
        title="Previous image"
        @click="showPrev"
      >
        <i class="bi bi-chevron-left" />
      </button>
      <button
        v-if="hasNext"
        type="button"
        class="pdp-grid__lightbox-nav pdp-grid__lightbox-nav--next"
        title="Next image"
        @click="showNext"
      >
        <i class="bi bi-chevron-right" />
      </button>
      <div class="pdp-grid__lightbox-stage">
        <img
          :src="lightboxTile.url"
          class="pdp-grid__lightbox-img"
          :alt="lightboxTile.label"
        >
        <img
          v-if="lightboxTile.overlayUrl"
          :src="lightboxTile.overlayUrl"
          class="pdp-grid__lightbox-overlay"
          alt="Product"
        >
        <div
          v-if="lightboxTile._mrhiCallouts && lightboxTile._mrhiCallouts.type === 'cambridge'"
          class="pdp-grid__lightbox-callouts"
        >
          <div class="pdp-grid__lightbox-callout-flavor">
            {{ lightboxTile._mrhiCallouts.flavorText }}
          </div>
          <div class="pdp-grid__lightbox-callout-count">
            {{ lightboxTile._mrhiCallouts.countText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BUILT_IN_TYPES } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { downloadFileFromUrl, downloadAttachmentComposite } from '@frontend/utils';

export default {
  name: 'AIChatWidgetPdpParallelGridLegacy',
  props: {
    id: {
      type: String,
      default: 'pdp_parallel_grid',
    },
    grid: {
      type: Object,
      required: true,
    },
    messageIndex: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      hovered: null,
      lightboxTile: null,
      addableTypesOpen: false,
      dropdownOpensDown: false,
    };
  },
  computed: {
    tiles() {
      return (this.grid && this.grid.tiles) || [];
    },
    addableTypes() {
      const present = new Set(this.tiles.map((t) => t.type));

      return Object.values(BUILT_IN_TYPES)
        .filter((t) => !present.has(t.key))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    generatingCount() {
      return this.tiles.filter((t) => t.state === 'generating' || t.state === 'regenerating').length;
    },
    showStop() {
      return !this.grid.stopped && this.tiles.some((t) => t.state === 'queued' || t.state === 'generating');
    },
    canCreate() {
      const usable = this.tiles.filter((t) => t.state !== 'skipped' && t.state !== 'stopped');
      return usable.length > 0 && usable.every((t) => t.state === 'done');
    },
    // Tiles the lightbox can actually show — same rule openTile uses — in
    // display order. Prev/next steps through these so we never land on a
    // blank/failed/still-generating tile. (POR-13637)
    openableTiles() {
      return this.tiles.filter((t) => t && t.state === 'done' && t.url);
    },
    lightboxIndex() {
      return this.lightboxTile ? this.openableTiles.indexOf(this.lightboxTile) : -1;
    },
    hasPrev() {
      return this.lightboxIndex > 0;
    },
    hasNext() {
      return this.lightboxIndex > -1 && this.lightboxIndex < this.openableTiles.length - 1;
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentMousedown);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown);
    document.removeEventListener('keydown', this.onLightboxKeydown);
  },
  methods: {
    // Emit the raw action string. AIChatMessage forwards it verbatim through
    // onWidgetAction(widget.id, $event) — the same `@action` convention used by
    // the other single-action widgets (action_buttons, template_slider, etc.).
    act(action) {
      this.$emit('action', action);
    },
    addType(key) {
      this.addableTypesOpen = false;
      this.act(`pdp_grid_add_type:${key}`);
    },
    toggleAddDropdown() {
      this.addableTypesOpen = !this.addableTypesOpen;

      if (this.addableTypesOpen) {
        this.$nextTick(this.updateDropdownPosition);
      }
    },
    updateDropdownPosition() {
      const trigger = this.$refs.addTrigger;

      if (!trigger) {
        return;
      }

      const rect = trigger.getBoundingClientRect();
      const estimatedHeight = 240;
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;

      this.dropdownOpensDown = spaceAbove < estimatedHeight && spaceBelow > spaceAbove;
    },
    onDocumentMousedown(event) {
      if (!this.addableTypesOpen) {
        return;
      }

      const inside = event.target.closest && event.target.closest('.pdp-grid__add-tile');

      if (!inside) {
        this.addableTypesOpen = false;
      }
    },
    // Use-as-is tiles (the original packshot or the user's own upload) are not
    // AI-generated, so they must not carry the "✦ AI" badge (POR-13366).
    isUseAsIs(tile) {
      return tile.mode === 'original' || tile.mode === 'yours';
    },
    badgeText(tile) {
      if (tile.mode === 'gallery') return 'Gallery';
      if (tile.mode === 'original') return 'Original';
      if (tile.mode === 'yours') return 'Yours';
      return '✦ AI';
    },
    // Layered vs Static (product-image) toggle, shown on hover for AI Lifestyle
    // tiles. Switching the mode REGENERATES the tile (separate = product-free
    // background so the product can be layered without duplicating it). Scene
    // tiles are excluded: composeSceneTileUrl always bakes the product into the
    // composite, so they can't be layered without a duplicate — they stay Static.
    // Info panels (noOverlay) and separate-default hero tiles are also excluded. (POR-13271)
    canToggleProductMode(tile) {
      if (!tile || tile.state !== 'done') return false;
      if (tile.mode !== 'ai') return false;
      const def = BUILT_IN_TYPES[tile.type];
      if (def && def.noOverlay) return false;
      return tile.type === 'lifestyle';
    },
    tileProductMode(tile) {
      return (tile.context && tile.context.productMode) || 'integrated';
    },
    openTile(tile) {
      if (tile && tile.state === 'done' && tile.url) {
        this.lightboxTile = tile;
        document.addEventListener('keydown', this.onLightboxKeydown);
      }
    },
    closeLightbox() {
      this.lightboxTile = null;
      document.removeEventListener('keydown', this.onLightboxKeydown);
    },
    // Step to the previous/next openable tile without leaving the lightbox.
    // hasPrev/hasNext guard the boundaries (arrows are hidden at the ends). (POR-13637)
    showPrev() {
      if (this.hasPrev) this.lightboxTile = this.openableTiles[this.lightboxIndex - 1];
    },
    showNext() {
      if (this.hasNext) this.lightboxTile = this.openableTiles[this.lightboxIndex + 1];
    },
    onLightboxKeydown(e) {
      if (!this.lightboxTile) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.showPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.showNext();
      } else if (e.key === 'Escape') {
        this.closeLightbox();
      }
    },
    // Download the previewed tile. Mirrors AIChatMessage.onDownloadAttachment so
    // the saved file matches what the lightbox shows: when the tile has a product
    // overlay (separate/layered mode) or Cambridge callouts, composite them onto
    // the base so the download isn't a blank white box; otherwise download the
    // image directly. (POR-13627)
    async downloadLightboxImage() {
      const tile = this.lightboxTile;
      if (!tile || !tile.url) return;
      const baseName = (tile.label || 'ai-studio-image').toString().trim().replace(/\s+/g, '-').toLowerCase();
      const fileName = `${baseName || 'ai-studio-image'}.png`;
      const hasOverlay = !!tile.overlayUrl;
      const hasCallouts = !!(tile._mrhiCallouts && tile._mrhiCallouts.type === 'cambridge');
      if (hasOverlay || hasCallouts) {
        await downloadAttachmentComposite(
          { payload: tile.url, overlayUrl: tile.overlayUrl, _mrhiCallouts: tile._mrhiCallouts },
          fileName,
        );
      } else {
        await downloadFileFromUrl(tile.url, fileName);
      }
    },
  },
};
</script>

<style scoped>
/* Transparent + zero padding so the widget blends into the assistant chat
   bubble (background #F2F5F9) as a single container instead of a nested green
   box, and spans the full bubble width (via hasWideWidget). (POR-13183) */
.pdp-grid { background: transparent; padding: 0; }
.pdp-grid__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; gap: 8px; }
.pdp-grid__count { font-size: 13px; color: #2b3a36; }
.pdp-grid__generating { color: #6b7c77; }
.pdp-grid__actions { display: flex; gap: 8px; align-items: center; }
.pdp-grid__stop { display: inline-flex; align-items: center; gap: 4px; background: none; border: none; color: #d9534f; font-weight: 600; font-size: 13px; cursor: pointer; padding: 4px 6px; }
.pdp-grid__stop i { font-size: 12px; }
.pdp-grid__stop:hover { color: #c9302c; }
.pdp-grid__btn { background: #fff; border: 1px solid #cdd6d3; border-radius: 8px; padding: 6px 12px; font-size: 13px; cursor: pointer; }
.pdp-grid__btn--primary { background: #1f8a6d; border-color: #1f8a6d; color: #fff; }
.pdp-grid__btn:disabled { opacity: .5; cursor: default; }
.pdp-grid__tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.pdp-grid__tile { position: relative; aspect-ratio: 1; background: #dfe8e5; border-radius: 10px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.pdp-grid__tile--generating { background: #d6e2de; }
.pdp-grid__badge { position: absolute; top: 8px; right: 8px; z-index: 1; background: #1f8a6d; color: #fff; font-size: 11px; border-radius: 6px; padding: 2px 6px; }
.pdp-grid__badge--gallery { background: #8a97a0; }
.pdp-grid__badge--plain { background: #8a97a0; }
.pdp-grid__img { width: 100%; height: 100%; object-fit: cover; cursor: pointer; }
.pdp-grid__overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); max-width: 86%; max-height: 86%; width: auto; height: auto; object-fit: contain; pointer-events: none; }
.pdp-grid__placeholder { display: flex; flex-direction: column; align-items: center; gap: 6px; color: #2b3a36; }
.pdp-grid__placeholder .bi-stars { font-size: 26px; color: #1f8a6d; }
.pdp-grid__spark--active { animation: spin 1.4s linear infinite; }
.pdp-grid__spark--queued { opacity: 0.4; }
.pdp-grid__label { font-size: 12px; text-align: center; padding: 0 6px; line-height: 1.3; }
.pdp-grid__hover { position: absolute; left: 6px; right: 6px; bottom: 6px; display: flex; gap: 6px; align-items: center; }
.pdp-grid__hover-btn { display: inline-flex; align-items: center; gap: 3px; background: #fff; border: 1px solid #cdd6d3; border-radius: 6px; padding: 2px 7px; font-size: 10px; line-height: 1.2; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1 1 auto; min-width: 0; justify-content: center; }
.pdp-grid__hover-btn i { font-size: 11px; }
.pdp-grid__hover-x { flex: 0 0 auto; background: #fff; border: 1px solid #cdd6d3; border-radius: 6px; padding: 2px 6px; font-size: 10px; line-height: 1.2; cursor: pointer; }
.pdp-grid__mode { position: absolute; top: 6px; left: 6px; right: 6px; display: flex; gap: 4px; z-index: 4; }
.pdp-grid__mode-btn { flex: 1; background: rgba(255, 255, 255, 0.94); border: 1px solid #cdd6d3; border-radius: 6px; padding: 1px 4px; font-size: 9px; line-height: 1.5; cursor: pointer; color: #475569; }
.pdp-grid__mode-btn.is-active { background: #379C75; border-color: #379C75; color: #fff; }
.pdp-grid__mode-btn:hover:not(.is-active) { border-color: #94a3b8; color: #1a1a2e; }
.pdp-grid__skipped, .pdp-grid__error { position: absolute; bottom: 8px; font-size: 12px; color: #6b7c77; }

.pdp-grid__add-tile {
  background: transparent;
  border: 2px dashed #cdd6d3;
  overflow: visible;
}

.pdp-grid__add-tile:hover {
  border: 2px dashed #1f8a6d;
}

.pdp-grid__add-trigger {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: none;
  border: none;
  color: #475569;
  font-size: 13px;
  cursor: pointer;
}

.pdp-grid__add-trigger i {
  font-size: 20px;
}

.pdp-grid__add-trigger:hover,
.pdp-grid__add-trigger.is-open {
  color: #1f8a6d;
}

.pdp-grid__add-dropdown-menu {
  position: absolute;
  bottom: calc(100% - 148px);
  min-width: 180px;
  max-height: fit-content;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  overflow-y: auto;
  z-index: 1;
  padding: 4px;
}

.pdp-grid__add-dropdown-menu--down {
  top: calc(100% - 148px);
}

.pdp-grid__add-dropdown-header {
  padding: 7px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.pdp-grid__add-item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 7px 12px;
  background: transparent;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  color: #334155;
  text-align: left;
  white-space: nowrap;
}

.pdp-grid__add-item i {
  font-size: 11px;
  color: #94a3b8;
}

.pdp-grid__add-item:hover {
  background: #f1f5f9;
}

/* Cambridge MRHI callouts: two black rectangles on the right edge — the flavor/variant
   (rotated 90°) in the top box and the count/size in the bottom box. Mirrors the
   in-editor .message__attachment-cambridge-* styling so the grid tile shows the same
   styled preview instead of unstyled floating placeholder text. (POR-13612) */
.pdp-grid__callouts { position: absolute; inset: 0; pointer-events: none; z-index: 3; }
.pdp-grid__callouts::before,
.pdp-grid__callouts::after { content: ''; position: absolute; right: 0; width: 22%; background: #000; z-index: 0; }
.pdp-grid__callouts::before { top: 0; height: 73%; }
.pdp-grid__callouts::after { top: 78%; height: 22%; }
.pdp-grid__callout-flavor,
.pdp-grid__callout-count { position: absolute; left: 89%; color: #fff; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; white-space: nowrap; z-index: 1; }
.pdp-grid__callout-flavor { top: 36.5%; transform: translate(-50%, -50%) rotate(-90deg); font-size: 11px; }
.pdp-grid__callout-count { top: 89%; transform: translate(-50%, -50%); font-size: 9px; text-align: center; }
.pdp-grid__tile-spinner { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.spin { animation: spin 1s linear infinite; }
.pdp-grid__lightbox { position: fixed; inset: 0; z-index: 2000; background: rgba(17,24,39,0.72); display: flex; align-items: center; justify-content: center; }
.pdp-grid__lightbox-stage { position: relative; display: inline-block; line-height: 0; background: #fff; border-radius: 8px; }
.pdp-grid__lightbox-img { display: block; max-width: 88vw; max-height: 88vh; width: auto; height: auto; }
.pdp-grid__lightbox-overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); max-width: 86%; max-height: 86%; width: auto; height: auto; object-fit: contain; }
.pdp-grid__lightbox-close { position: absolute; top: 18px; right: 22px; z-index: 1; background: rgba(255,255,255,0.9); border: none; border-radius: 50%; width: 36px; height: 36px; font-size: 16px; cursor: pointer; color: #111827; }
.pdp-grid__lightbox-download { position: absolute; top: 18px; right: 66px; z-index: 1; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.9); border: none; border-radius: 50%; width: 36px; height: 36px; font-size: 15px; cursor: pointer; color: #111827; }
.pdp-grid__lightbox-download:hover { background: #fff; }
/* Prev/next arrows on the left/right edges — hidden at the ends via v-if. (POR-13637) */
.pdp-grid__lightbox-nav { position: absolute; top: 50%; transform: translateY(-50%); z-index: 1; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.9); border: none; border-radius: 50%; width: 44px; height: 44px; font-size: 20px; cursor: pointer; color: #111827; }
.pdp-grid__lightbox-nav:hover { background: #fff; }
/* Bootstrap Icons ship with their own line-height/vertical-align that pushes the
   glyph off-centre; reset it so the flexbox centres the chevron in the circle. */
.pdp-grid__lightbox-nav i { display: block; line-height: 1; }
.pdp-grid__lightbox-nav--prev { left: 22px; }
.pdp-grid__lightbox-nav--next { right: 22px; }
/* Cambridge MRHI callouts in the enlarged lightbox (larger type than the tile). (POR-13612) */
.pdp-grid__lightbox-callouts { position: absolute; inset: 0; pointer-events: none; z-index: 2; }
.pdp-grid__lightbox-callouts::before,
.pdp-grid__lightbox-callouts::after { content: ''; position: absolute; right: 0; width: 22%; background: #000; z-index: 0; }
.pdp-grid__lightbox-callouts::before { top: 0; height: 73%; }
.pdp-grid__lightbox-callouts::after { top: 78%; height: 22%; }
.pdp-grid__lightbox-callout-flavor,
.pdp-grid__lightbox-callout-count { position: absolute; left: 89%; color: #fff; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; white-space: nowrap; z-index: 1; }
.pdp-grid__lightbox-callout-flavor { top: 36.5%; transform: translate(-50%, -50%) rotate(-90deg); font-size: 32px; }
.pdp-grid__lightbox-callout-count { top: 89%; transform: translate(-50%, -50%); font-size: 22px; text-align: center; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 640px) { .pdp-grid__tiles { grid-template-columns: repeat(2, 1fr); } }
</style>
